/*
    CmsProvider.jsx
    microCMS からページ内容を取得して各ページに渡す仕組み。

    - microCMS の設定（.env）が無い／取得に失敗した場合は、
      これまで通り locales/ja.json・en.json の文章で表示されます。
    - 日本語表示：microCMS の内容をそのまま使います（公開するとすぐ反映）。
    - 英語表示：デプロイ時に tools/translate-cms.mjs が DeepL で英訳した
      cms-en.json を使います（microCMS で公開すると自動で英訳・デプロイ）。
      英訳が無い欄は従来どおり en.json の英語で表示されます。
*/

import React, { createContext, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import cmsEn from "./cms-en.json";

const SERVICE = process.env.REACT_APP_MICROCMS_SERVICE_DOMAIN;
const API_KEY = process.env.REACT_APP_MICROCMS_API_KEY;

// microCMS 側で作る API（すべて「オブジェクト形式」）
export const CMS_ENDPOINTS = ["site", "home", "about", "programs", "donate"];

// 番号付きの欄（例：biz1Title, biz2Title …）をまとめて1つのリストとして扱う設定
export const SLOTS = {
    businesses: { prefix: "biz", max: 4, keys: { title: "Title", description: "Desc", items: "Items", image: "Image" } },
    grapeFeatures: { prefix: "feature", max: 6, keys: { title: "Title", description: "Desc" } },
    priceCards: {
        prefix: "price", max: 4,
        keys: { title: "Title", label1: "Label1", price1: "Price1", label2: "Label2", price2: "Price2", label3: "Label3", price3: "Price3", note: "Note" },
    },
    boardMembers: { prefix: "member", max: 8, keys: { title: "Name", name: "Name", role: "Role", profession: "Job" } },
    sponsors: { prefix: "sponsor", max: 6, keys: { title: "Name", logo: "Logo", adjust: "Adjust", tier: "Tier" } },
    donationOptions: { prefix: "option", max: 8, keys: { title: "Title", description: "Desc", impact: "Impact", amount: "Amount" } },
};

export const CmsContext = createContext({ data: {}, loaded: true });

async function fetchEndpoint(endpoint) {
    const res = await fetch(`https://${SERVICE}.microcms.io/api/v1/${endpoint}`, {
        headers: { "X-MICROCMS-API-KEY": API_KEY },
    });
    if (!res.ok) throw new Error(`${endpoint}: ${res.status}`);
    return res.json();
}

export function CmsProvider({ children }) {
    const enabled = Boolean(SERVICE && API_KEY);
    const [state, setState] = useState({ data: {}, loaded: !enabled });

    useEffect(() => {
        if (!enabled) return;
        let cancelled = false;
        Promise.allSettled(CMS_ENDPOINTS.map(fetchEndpoint)).then((results) => {
            if (cancelled) return;
            const data = {};
            results.forEach((r, i) => {
                if (r.status === "fulfilled") data[CMS_ENDPOINTS[i]] = r.value;
                else console.warn("[microCMS] 取得失敗:", r.reason);
            });
            setState({ data, loaded: true });
        });
        return () => {
            cancelled = true;
        };
    }, [enabled]);

    return <CmsContext.Provider value={state}>{children}</CmsContext.Provider>;
}

/* ---------- ページで使うヘルパー ---------- */

const isBlank = (v) =>
    v === undefined || v === null || (typeof v === "string" && v.trim() === "") ||
    (Array.isArray(v) && v.length === 0);

/** テキストエリアを「1行 = 1項目」の配列にする */
export const toLines = (text) =>
    typeof text === "string"
        ? text.split(/\r?\n/).map((s) => s.trim()).filter(Boolean)
        : [];

/** テキストエリアを「空行区切り = 1段落」の配列にする */
export const toParagraphs = (text) =>
    typeof text === "string"
        ? text.split(/\r?\n\s*\r?\n/).map((s) => s.trim()).filter(Boolean)
        : [];

/**
 * ページ用フック
 *   const { text, image, list, items, url } = useCms("home");
 *   text("heroTitle", "homePage.hero.title")       … 文字（CMS が空なら翻訳ファイル）
 *   image("heroImage", defaultImg)                 … 画像 URL
 *   list("activities", ["aboutPage.activities.item1", ...]) … 1行1項目のリスト
 *   paragraphs("story", [...keys])                 … 段落のリスト
 *   items("businesses", fallbackArray)             … 繰り返しフィールド
 *   url("trialFormUrl", "https://...")             … リンク先
 */
export function useCms(endpoint) {
    const { t, i18n } = useTranslation();
    const { data, loaded } = useContext(CmsContext);
    const isJa = (i18n.language || "ja").startsWith("ja");
    // 英語表示では、英訳済みの CMS コンテンツ（cms-en.json）があればそれを使う
    const enPage = !isJa && cmsEn && cmsEn[endpoint];
    const useCmsText = isJa || Boolean(enPage);
    const page = (isJa ? data[endpoint] : enPage) || {};
    const site = (!isJa && cmsEn && cmsEn.site) || data.site || {};

    const raw = (field) => page[field];

    const text = (field, key) =>
        useCmsText && !isBlank(page[field]) ? page[field] : t(key);

    const image = (field, fallback) => page[field]?.url || fallback;

    const url = (field, fallback) => (!isBlank(page[field]) ? page[field] : fallback);

    const list = (field, keys) =>
        useCmsText && !isBlank(toLines(page[field])) ? toLines(page[field]) : keys.map((k) => t(k));

    const paragraphs = (field, keys) =>
        useCmsText && !isBlank(toParagraphs(page[field]))
            ? toParagraphs(page[field])
            : keys.map((k) => t(k));

    // 繰り返し項目：microCMS では「事業1」「事業2」…のような番号付きの欄で管理
    // （見出しが空の番号は表示しません）
    const items = (field, fallback) => {
        if (!useCmsText) return fallback;
        if (Array.isArray(page[field]) && page[field].length > 0) return page[field];
        const slot = SLOTS[field];
        if (!slot) return fallback;
        const found = [];
        for (let n = 1; n <= slot.max; n++) {
            const item = {};
            Object.entries(slot.keys).forEach(([key, suffix]) => {
                item[key] = page[`${slot.prefix}${n}${suffix}`];
            });
            if (!isBlank(item.title)) found.push(item);
        }
        return found.length > 0 ? found : fallback;
    };

    return { t, loaded, raw, text, image, url, list, paragraphs, items, site, isJa, cmsOn: useCmsText };
}
